import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser as apiLoginUser } from '../Services/Api/authService'; 

const AuthContext = createContext(null);

// Helper function to safely parse JSON from localStorage
const loadJSONFromStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error parsing JSON from localStorage key "${key}":`, error);
    localStorage.removeItem(key); // Clear invalid item
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('accessToken') || null); 
  const [user, setUser] = useState(loadJSONFromStorage('userData') || null); 
  const [isLoading, setIsLoading] = useState(false); 
  const [authError, setAuthError] = useState(null); 
  const navigate = useNavigate();

  // Effect to handle token/user changes in localStorage
  useEffect(() => {
    if (token && user) {
      localStorage.setItem('accessToken', token);
      localStorage.setItem('userData', JSON.stringify(user)); 
    } else {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userData');
    }
  }, [token, user]);

  // Global fetch wrapper: attaches Authorization header and handles 401 -> force logout
  useEffect(() => {
    const originalFetch = window.fetch && window.fetch.bind(window);
    if (!originalFetch) return;

    const wrappedFetch = async (input, init = {}) => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const newInit = { ...init };
        newInit.headers = {
          ...(init && init.headers ? init.headers : {}),
        };

        if (accessToken) {
          newInit.headers = { ...newInit.headers, Authorization: `Bearer ${accessToken}` };
        }

        const response = await originalFetch(input, newInit);

        if (response.status === 401) {
          console.warn('API returned 401 — logging out and redirecting to /login');
          // Clear auth state and localStorage
          setUser(null);
          setToken(null);
          // Navigate to login page
          try {
            navigate('/login', { replace: true });
          } catch (navErr) {
            console.error('Navigation to /login failed:', navErr);
          }
        }

        return response;
      } catch (err) {
        throw err;
      }
    };

    // Replace global fetch with wrapper
    // eslint-disable-next-line no-undef
    window.fetch = wrappedFetch;

    return () => {
      // restore original
      if (originalFetch) {
        window.fetch = originalFetch;
      }
    };
  }, [navigate]);

  // Login function
  const login = async (credentials) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      // credentials object contains { email: '...', password: '...' }
      const data = await apiLoginUser(credentials); 
      
      // Map the new backend response to our frontend user structure
      const userData = {
        id: data.user.id,
        firstName: data.user.first_name,
        lastName: data.user.last_name,
        email: data.user.email,
        username: data.user.username,
        roles: Array.isArray(data.user.role) ? data.user.role : [data.user.role], // <-- store all roles as array
        role: Array.isArray(data.user.role) ? data.user.role[0] : data.user.role, // keep for backward compatibility
        company: {
          id: data.user.company.company_id,
          name: data.user.company.company_name,
          type: data.user.company.company_type,
          registration_number: data.user.company.company_registration_number,
          address: data.user.company.company_address,
          logo_url: data.user.company.company_logo_url
        },
        dateJoined: data.user.date_joined,
        lastLogin: data.user.last_login
      };
      
      console.log("Storing user data in context:", userData);
      
      setUser(userData); 
      setToken(data.token); // Use token from response instead of access

      setIsLoading(false);
      return true; 
    } catch (error) {
      console.error("AuthContext Login Error:", error);
      setAuthError(error.message || 'Login failed.');
      // Clear state on error
      setUser(null); 
      setToken(null); 
      setIsLoading(false);
      return false; 
    }
  };

  // Logout function remains the same
  const logout = () => {
    setUser(null); 
    setToken(null); 
    setAuthError(null);
    console.log("User logged out");
  };

  // Value provided to consuming components
  const value = {
    user,
    token,
    isLoggedIn: !!token && !!user, 
    isLoading, 
    authError,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
